import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useMemo } from 'react';
import { SelectElement, TextFieldElement, useFormContext } from 'react-hook-form-mui';
import { useClientFields } from '../../../sic-lib/api/hooks/useClientFields';
import { FieldValidationType } from '../../../sic-lib/enums/FieldValidationType';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../Shared/ButtonLoading';
import PhoneNumberTextFieldElement from '../../Shared/FormControls/Inputs/PhoneNumberTextFieldElement';
import QuickCallConsent from './QuickCallConsent';

interface Props {
    containerRef: React.RefObject<HTMLDivElement>;
    isLoading: boolean;
    isError: boolean;
    error: any;
}

export function QuickCallCreateContactFormControls({
    containerRef,
    isLoading,
    isError,
    error,
}: Props) {
    const { data: clientFields, isLoading: isLoadingClientFields } = useClientFields();
    const {
        formState: { isValid },
    } = useFormContext();

    const phoneFields = useMemo(() => {
        return (
            clientFields
                ?.filter(field => field.validationType === FieldValidationType.usMobilePhone)
                ?.map(field => ({
                    id: field.fieldId,
                    label: field.label,
                })) || []
        );
    }, [clientFields]);

    return (
        <Grid container item flexDirection="column" spacing={2} p={2}>
            <Grid item display="flex" justifyContent="center">
                <Typography variant="h5">New Contact</Typography>
            </Grid>
            <Grid container item flexDirection="row" justifyContent="center" spacing={2}>
                <Grid item xs={6}>
                    <TextFieldElement
                        name="firstName"
                        label="First Name"
                        variant="outlined"
                        fullWidth
                        required
                        size="small"
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextFieldElement
                        name="lastName"
                        label="Last Name"
                        variant="outlined"
                        fullWidth
                        size="small"
                    />
                </Grid>
                <Grid item xs={6}>
                    {isLoadingClientFields && (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                        >
                            <CircularProgress size={20} />
                        </Box>
                    )}
                    {!isLoadingClientFields && (
                        <SelectElement
                            name="phoneFieldId"
                            label="Field"
                            variant="outlined"
                            fullWidth
                            required
                            options={phoneFields}
                            size="small"
                        />
                    )}
                </Grid>
                <Grid item xs={6}>
                    <PhoneNumberTextFieldElement
                        name="phoneNumber"
                        label="Phone Number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        required
                        disabled
                    />
                </Grid>
            </Grid>
            <Grid item>
                <QuickCallConsent parentRef={containerRef} />
            </Grid>
            {isError && (
                <Grid item>
                    <AxiosErrorAlert error={error} />
                </Grid>
            )}
            <Grid item>
                <ButtonLoading
                    variant="contained"
                    color="success"
                    type="submit"
                    disabled={!isValid}
                    isLoading={isLoading}
                    isError={isError}
                    fullWidth
                >
                    Create &amp; Call
                </ButtonLoading>
            </Grid>
        </Grid>
    );
}
